import { useForm } from "react-hook-form";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Axios from "../../utils/Axios";
import { useSetRecoilState } from "recoil";
import {
  toastShowState,
  toastBgState,
  toastMessageState,
  toastAutoHideState,
} from "../../utils/atoms/atoms";

interface IBulkUpdateModalProps {
  // Required props
  downloadEndpoint: string;
  uploadEndpoint: string;
  guidelines: string[];
  successMessage: string;

  // Optional props
  acceptFileTypes?: string;
  modalTitle?: string;
  downloadButtonText?: string;
  fileName?: string;
  submitButtonText?: string;
  additionalFormData?: Record<string, any>;
}

const BulkUpdateModalBody = ({
  downloadEndpoint,
  uploadEndpoint,
  guidelines,
  successMessage,
  acceptFileTypes = ".xlsx",
  modalTitle = "Bulk Update",
  downloadButtonText = "Download",
  submitButtonText = "Submit",
  fileName = "template.xlsx",
  additionalFormData = {},
}: IBulkUpdateModalProps) => {
  const queryClient = useQueryClient();
  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);
  const setAutoHide = useSetRecoilState(toastAutoHideState);

  const [fileInput, setFileInput] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const showToast = (
    type: "success" | "danger" | "warner" | "info",
    message: string
  ) => {
    setToastShow(true);
    setAutoHide(true);
    setToastBg(type);
    setToastMessage(message);
  };

  const downloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const response = await Axios.get(downloadEndpoint, {
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showToast("success", "Template downloaded successfully");
    } catch (error) {
      showToast("danger", "Failed to download template");
    } finally {
      setIsDownloading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileInput(e.target.value);
  };

  const onSubmit = async (data: any) => {
    if (!data.excel_file || data.excel_file.length === 0) {
      showToast("danger", "Please select a file");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("excel_file", data.excel_file[0]);

      // Append additional data if needed
      Object.entries(additionalFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      await Axios.post(uploadEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Clear form and show success
      //reset();
      setFileInput("");
      showToast("success", successMessage);

      // Invalidate relevant queries
      queryClient.invalidateQueries();
    } catch (error: any) {
      const errorMsg = "Something went wrong";

      showToast("danger", errorMsg);
      if (error.response?.data && error.response.data?.errors) {
        const url = window.URL.createObjectURL(
          new Blob([JSON.stringify(error.response.data.errors, null, "\t")])
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "errors.json");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container">
      <h3 className="text-center mb-4">{modalTitle}</h3>

      <div className="row">
        <div className="col-md-6">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center gap-2">
              <label className="fw-medium">Download template:</label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={downloadTemplate}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                ) : (
                  <>
                    {downloadButtonText} <i className="bi bi-download ms-1" />
                  </>
                )}
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-3">
                <label htmlFor="file-input" className="form-label">
                  Upload filled template:
                </label>
                <input
                  id="file-input"
                  type="file"
                  className="form-control"
                  accept={acceptFileTypes}
                  {...register("excel_file", {
                    required: "File is required",
                  })}
                  onChange={handleFileChange}
                  value={fileInput}
                />
                {errors.excel_file && (
                  <div className="text-danger">
                    {errors.excel_file.message?.toString()}
                  </div>
                )}
              </div>

              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    />
                  ) : (
                    submitButtonText
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">Guidelines</h5>
            </div>
            <div className="card-body">
              <ul className="list-group list-group-flush">
                {guidelines.map((guideline, index) => (
                  <li key={index} className="list-group-item">
                    {guideline}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateModalBody;
